import {
  AclProfile,
  BackendService,
  ProxyTemplate,
  ContentFilterProfile,
  ContentFilterRule,
  Action,
  DynamicRule,
  EdgeFunction,
  FlowControlPolicy,
  GlobalFilter,
  HttpRequestMethods,
  MobileApplicationGroup,
  RateLimitRule,
  RoutingProfile,
  SecurityPolicy,
  ServerGroup,
  SecurityAlert,
  LogExporter,
  ClientCaCertificate,
  ServerCertificate,
  ServerToBackendMtlsCertificate,
  ServerToBackendCaCertificate,
} from '@/types';
import _ from 'lodash';

const titles: { [key: string]: string } = {
  admin: 'Admin',
  allow: 'Block - Skip',
  allow_bot: 'Bot Challenge - Skip',
  allow_deny_bot: 'Bot Challenge',
  allow_deny: 'Block',
  args: 'Arguments',
  attrs: 'Attributes',
  'audit-log': 'Audit Log',
  passthrough: 'Bypass',
  cookies: 'Cookies',
  'curiefense-lists': 'Curiefense Lists',
  customsigs: 'Custom Signatures',
  deny: 'Block - Apply',
  deny_bot: 'Bot Challenge - Skip',
  'events-and-attacks': 'Events & Attacks',
  'external-lists': 'External Lists',
  force_deny: 'Enforce Deny',
  headers: 'Headers',
  names: 'Name',
  reg: 'Regex',
  regex: 'Regex',
  'saml2-sso': 'SAML2 SSO',
  'top-activities': 'Top Activities',
  'traffic-overview': 'Traffic Overview',
  'update-log': 'Update log',
  'version-control': 'Version Control',
  include: 'Include',
  exclude: 'Exclude',
  'headers-entry': 'Header',
  'cookies-entry': 'Cookie',
  'args-entry': 'Argument',
  'attrs-entry': 'Attribute',
  'acl-profiles': 'ACL Profiles',
  'acl-profiles-singular': 'ACL Profile',
  'dynamic-rules': 'Dynamic Rules',
  'dynamic-rules-singular': 'Dynamic Rule',
  'rate-limit-rules': 'Rate Limit Rules',
  'rate-limit-rules-singular': 'Rate Limit Rule',
  'security-policies': 'Security Policies',
  'security-policies-singular': 'Security Policy',
  'content-filter-profiles': 'Content Filter Profiles',
  'content-filter-profiles-singular': 'Content Filter Profile',
  'content-filter-rules': 'Content Filter Rules',
  'content-filter-rules-singular': 'Content Filter Rule',
  'edge-functions': 'Edge Functions',
  'edge-functions-singular': 'Edge Function',
  'global-filters': 'Global Filters',
  'global-filters-singular': 'Global Filter',
  quarantined: 'Quarantined List',
  'flow-control-policies': 'Flow Control Policies',
  'flow-control-policies-singular': 'Flow Control Policy',
  actions: 'Actions',
  'actions-singular': 'Action',
  active: 'Active',
  'routing-profiles': 'Routing Profiles',
  'routing-profiles-singular': 'Routing Profile',
  'mobile-application-groups': 'Mobile Application Groups',
  'mobile-application-groups-singular': 'Mobile Application Group',
  'proxy-templates': 'Proxy Templates',
  'proxy-templates-singular': 'Proxy Template',
  'server-groups': 'Server Groups',
  'server-groups-singular': 'Server Group',
  'backend-services': 'Backends Services',
  'backend-services-singular': 'Backend Service',
  certificates: 'Certificates',
  'certificates-singular': 'Certificate',
  report: 'Report',
  ignore: 'Ignore',
  request: 'Request',
  response: 'Response',
  acl: 'ACL Profiles',
  'acl-singular': 'ACL Profile',
  cf: 'Content Filters',
  'cf-singular': 'Content Filter',
  cf_restrict: 'Content Filter Rules',
  'cf_restrict-singular': 'Content Filter Rule',
  gf: 'Global Filters',
  'gf-singular': 'Global Filter',
  rl: 'Rate Limit Rules',
  'rl-singular': 'Rate Limit Rule',
  dr: 'Dynamic Rules',
  'dr-singular': 'Dynamic Rule',
  challenge: 'Challenge',
  sso: 'SSO',
  tags: 'Tags',
  rule: 'Rule',
  allsections: 'All',
};

function generateUUID(): string {
  let dt = new Date().getTime();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = ((dt + Math.random() * 16) % 16) | 0;
    dt = Math.floor(dt / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function generateUUID2(): string {
  return generateUUID().split('-')[4];
}

const defaultFlowControlStep = {
  method: 'GET' as HttpRequestMethods,
  uri: '/',
  cookies: {},
  headers: {
    host: 'www.example.com',
  },
  args: {},
};

// Temporarily disable any, we should convert this factory to different functions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const newEntityFactory: { [key: string]: () => any } = {
  'acl-profiles'(): AclProfile {
    return {
      id: generateUUID2(),
      name: 'New ACL Profile',
      description: 'New ACL Profile description and remarks.',
      action: 'action-acl-block',
      tags: [],
      allow: [],
      allow_bot: [],
      deny_bot: [],
      passthrough: [],
      force_deny: [],
      deny: [],
    };
  },

  'log-exporter'(): LogExporter {
    return {
      id: generateUUID2(),
      name: 'New Log Exporter',
      active: true,
      filter: 'blocked',
      format: 'custom_syslog',
      include_request_data: false,
      server_groups: {
        all: true,
        ids: [],
      },
      connection: {
        host: '',
        port: 514,
        protocol: 'tcp',
        tls_mode: 'no_tls',
        certificate: {
          chain_data: [],
          cert_body: '',
          expired: false,
        },
      },
    };
  },

  'content-filter-profiles'(): ContentFilterProfile {
    return {
      id: generateUUID2(),
      name: 'New Content Filter Profile',
      description: 'New Content Filter Profile description and remarks.',
      action: 'action-contentfilter-block',
      tags: [],
      ignore_body: false,
      ignore_alphanum: true,
      allsections: {
        names: [],
        regex: [],
        text: [],
        max_count: 42,
        enable_max_count: false,
        max_length: 1024,
        enable_max_length: false,
      },
      headers: {
        names: [],
        regex: [],
        text: [],
        max_count: 42,
        enable_max_count: false,
        max_length: 1024,
        enable_max_length: false,
      },
      cookies: {
        names: [],
        regex: [],
        text: [],
        max_count: 42,
        enable_max_count: false,
        max_length: 1024,
        enable_max_length: false,
      },
      args: {
        names: [],
        regex: [],
        text: [],
        max_count: 512,
        enable_max_count: false,
        max_length: 1024,
        enable_max_length: false,
      },
      path: {
        names: [],
        regex: [],
        text: [],
        max_count: 42,
        enable_max_count: false,
        max_length: 1024,
        enable_max_length: false,
      },
      url: {
        names: [],
        text: [],
        regex: [],
        max_count: 42,
        enable_max_count: false,
        max_length: 1024,
        enable_max_length: false,
      },
      decoding: {
        base64: true,
        dual: true,
        html: false,
        unicode: false,
      },
      masking_seed: 'CHANGEME',
      graphql_path: '',
      content_type: [],
      active: [
        'cf-rule-risk:5',
        'cf-rule-risk:4',
        'cf-rule-risk:3',
        'cf-rule-subcategory:libinjection-xss',
      ],
      report: [
        'cf-rule-name:200003',
        'cf-rule-name:200160',
        'cf-rule-name:200161',
        'cf-rule-name:200162',
        'cf-rule-name:300008',
        'cf-rule-name:300009',
        'cf-rule-name:300010',
        'cf-rule-name:500001',
        'cf-rule-name:500002',
        'cf-rule-name:500110',
        'cf-rule-name:500111',
        'cf-rule-name:500112',
        'cf-rule-name:700010',
        'cf-rule-name:700011',
        'cf-rule-name:700012',
        'cf-rule-name:800013',
        'cf-rule-name:900004',
        'cf-rule-name:910000',
        'cf-rule-name:910001',
        'cf-rule-name:920005',
        'cf-rule-name:920006',
        'cf-rule-name:920007',
        'cf-rule-name:920008',
        'cf-rule-name:920009',
        'cf-rule-name:920010',
        'cf-rule-name:920011',
        'cf-rule-name:920012',
        'cf-rule-name:920013',
        'cf-rule-name:920014',
        'cf-rule-name:920015',
        'cf-rule-name:920016',
        'cf-rule-name:920017',
      ],
      ignore: ['let-s-encrypt', 'malformed-body'],
    };
  },

  'global-filters'(): GlobalFilter {
    return {
      id: generateUUID2(),
      name: 'New Global Filter',
      source: 'self-managed',
      mdate: '',
      description: 'New Global Filter description and remarks.',
      active: false,
      tags: [],
      action: 'action-monitor',
      rule: {
        relation: 'OR',
        entries: [],
      },
    };
  },

  'security-policies'(): SecurityPolicy {
    const id = generateUUID2();
    return {
      id: id,
      name: 'New Security Policy',
      description: 'New Security Policy description and remarks.',
      tags: [],
      session: [
        {
          attrs: 'ip',
        },
      ],
      session_ids: [],
      map: [
        {
          id: '__default_entry__',
          match: '/',
          name: 'Default',
          acl_profile: '__acldefault__',
          content_filter_profile: '__defaultcontentfilter__',
          backend_service: '__default__',
          acl_profile_active: false,
          content_filter_profile_active: false,
          rate_limit_rules: [],
          edge_functions: [],
        },
        {
          id: '__site_level__',
          match: '__site_level__',
          name: 'Site Level',
          acl_profile: '__acldefault__',
          content_filter_profile: '__defaultcontentfilter__',
          backend_service: '__default__',
          acl_profile_active: false,
          content_filter_profile_active: false,
          rate_limit_rules: [],
          edge_functions: [],
        },
      ],
    };
  },

  'rate-limit-rules'(): RateLimitRule {
    return {
      id: generateUUID2(),
      name: 'New Rate Limit Rule',
      global: false,
      active: false,
      description: 'New Rate Limit Rule description and remarks.',
      timeframe: 60,
      tags: [],
      threshold: 5,
      action: 'action-rate-limit-block',
      is_action_ban: false,
      ttl: 60 * 60,
      include: {
        relation: 'OR',
        tags: ['all'],
      },
      exclude: {
        relation: 'OR',
        tags: [],
      },
      key: [
        {
          attrs: 'securitypolicyname',
        },
        {
          attrs: 'securitypolicyentryname',
        },
        {
          attrs: 'session',
        },
      ],
      pairwith: {
        self: 'self',
      },
    };
  },

  'flow-control-policies'(): FlowControlPolicy {
    return {
      id: generateUUID2(),
      name: 'New Flow Control Policy',
      description: 'New Flow Control Policy description and remarks.',
      active: true,
      include: ['all'],
      exclude: [],
      timeframe: 60,
      tags: [],
      key: [
        {
          attrs: 'ip',
        },
      ],
      steps: [
        _.cloneDeep(defaultFlowControlStep),
        {
          ..._.cloneDeep(defaultFlowControlStep),
          method: 'POST' as HttpRequestMethods,
        },
      ],
    };
  },

  'edge-functions'(): EdgeFunction {
    const id = generateUUID2();
    return {
      id: id,
      name: 'New Edge Function',
      description: 'New Edge Function description and remarks.',
      phase: 'request_post',
      code: `-- begin custom code
        --action header
        ngx.header['foo'] = 'bar'`,
    };
  },

  'dynamic-rules'(): DynamicRule {
    const id = generateUUID2();
    return {
      id: id,
      name: 'New Dynamic Rule',
      description: 'New Dynamic Rule description and remarks.',
      timeframe: 60,
      threshold: 9999,
      active: false,
      action: 'action-monitor',
      tags: [],
      include: {
        relation: 'OR',
        tags: ['all'],
      },
      exclude: {
        relation: 'OR',
        tags: [],
      },
      ttl: 7200,
      target: 'ip',
      offload_ip_filtering: false,
    };
  },

  'content-filter-rules'(): ContentFilterRule {
    return {
      id: generateUUID2(),
      name: 'New Content Filter Rule',
      description: 'New Content Filter Rule description and remarks.',
      msg: 'Log message',
      operand: '',
      risk: 1,
      category: '',
      subcategory: '',
      tags: [],
    };
  },

  actions(): Action {
    return {
      id: generateUUID2(),
      name: 'New Action',
      description: 'New Action description and remarks.',
      tags: [],
      type: 'monitor',
      params: {
        headers: {},
      },
    };
  },

  'server-groups'(): ServerGroup {
    const id = generateUUID2();
    return {
      id: id,
      name: 'New Server Group',
      challenge_cookie_domain: '$host',
      description: 'New Server Group description and remarks.',
      server_names: ['www.example.com'],
      security_policy: '__default__',
      routing_profile: '__default__',
      proxy_template: '__default__',
      mobile_application_group: '',
      ssl_certificate: 'placeholder',
      client_certificate: '',
      client_certificate_mode: 'off',
    };
  },

  'routing-profiles'(): RoutingProfile {
    const id = generateUUID2();
    return {
      id: id,
      name: 'New Routing Profile',
      description: 'New Routing Profile description and remarks.',
      locations: [
        {
          id: generateUUID2(),
          path: '/',
          backend_id: '__default__',
          cloud_functions: [],
        },
      ],
    };
  },

  'mobile-application-groups'(): MobileApplicationGroup {
    const id = generateUUID2();
    return {
      id: id,
      name: 'New Mobile Application Group',
      description: 'New Mobile Application Group description and remarks.',
      uid_header: '',
      grace: '5',
      active_config: [
        {
          active: true,
          json: '{}',
          name: 'Default',
        },
      ],
      signatures: [],
    };
  },

  'proxy-templates'(): ProxyTemplate {
    const id = generateUUID2();
    return {
      id: id,
      name: 'New Proxy Template',
      description: 'New Proxy Template description and remarks.',
      acao_header: false,
      client_body_timeout: '5',
      client_header_timeout: '5',
      client_max_body_size: '150',
      client_header_buffer_size: '1',
      large_client_header_buffers_size: '32',
      large_client_header_buffers_count: '2',
      client_body_buffer_size: '4',
      advanced_configuration: [],
      custom_listener: false,
      keepalive_timeout: '660',
      limit_req_rate: '1200',
      limit_req_burst: '400',
      mask_headers: '',
      proxy_connect_timeout: '5',
      proxy_read_timeout: '60',
      proxy_send_timeout: '30',
      send_timeout: '5',
      ssl_ciphers:
        'ECDHE-ECDSA-CHACHA20-POLY1305:ECDHE-RSA-CHACHA20-POLY1305:ECDHE-ECDSA-AES256-GCM-SHA384:ECDHE-RSA-AES256-GCM-SHA384:DHE-RSA-AES256-GCM-SHA384:ECDHE-RSA-AES256-SHA384:ECDHE-ECDSA-AES256-SHA384:ECDHE-ECDSA-AES256-SHA:ECDHE-RSA-AES256-SHA:DHE-RSA-AES256-SHA256:DHE-RSA-AES256-SHA:!SHA1:!SHA256:!SHA384:!DSS:!aNULL',
      ssl_protocols: ['TLSv1.2', 'TLSv1.3'],
      upstream_host: '$host',
      xff_header_name: ['X-Forwarded-For'],
      xrealip_header_name: 'X-Real-IP',
    };
  },

  'backend-services'(): BackendService {
    const id = generateUUID2();
    return {
      id: id,
      name: 'New Backend Service',
      description: 'New Backend Service description and remarks.',
      least_conn: false,
      http11: true,
      transport_mode: 'default',
      sticky: 'none',
      back_hosts: [],
    };
  },

  'server-certificates'(): ServerCertificate {
    const id = generateUUID2();
    return {
      id: id,
      name: id,
      private_key: '',
      cert_body: '',
      side: 'server',
      revoked: false,
    };
  },

  'client-ca-certificates'(): ClientCaCertificate {
    const id = generateUUID2();
    return {
      id: id,
      name: id,
      cert_body: '',
      side: 'clientCA',
      revoked: false,
    };
  },

  'server-to-backend-mtls-certificates'(): ServerToBackendMtlsCertificate {
    const id = generateUUID2();
    return {
      id: id,
      name: id,
      private_key: '',
      cert_body: '',
      side: 'serverToBackendMTLS',
      revoked: false,
      le_auto_renew: false,
      le_auto_replace: false,
    };
  },

  'server-to-backend-ca-certificates'(): ServerToBackendCaCertificate {
    const id = generateUUID2();
    return {
      id: id,
      name: id,
      cert_body: '',
      side: 'backendCA',
      revoked: false,
      le_auto_renew: false,
      le_auto_replace: false,
    };
  },

  'security-alerts'(): SecurityAlert {
    const id = generateUUID2();
    return {
      id: id,
      name: 'New Security Alert',
      server_groups: ['__all__'],
      dynamic_rules: [],
      recipients: [],
    };
  },
};

export default {
  name: 'DatasetsUtils',
  titles,
  generateUUID,
  generateUUID2,
  newEntityFactory,
  defaultFlowControlStep,
};
